import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { OverlayScroll } from '../ui/overlay-scroll';
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useLockMobileViewportScale } from '../../hooks/useLockMobileViewportScale';
import { useResizableColumn } from '../../hooks/useResizableColumn';
import { AppHeader } from './AppHeader';
import { AppFooter } from './AppFooter';
import { ResizeHandle } from './ResizeHandle';
import {
  SidebarLayoutProvider,
  SidebarShellProvider,
  SIDEBAR_RESIZE_HANDLE_PX,
  SIDEBAR_CONTENT_GAP_PX,
  useSidebarShell,
} from './SidebarLayoutContext';
import {
  defaultFilterColumnWidth,
  fitPanes,
  PROXY_TRAY_DEFAULT_PX,
  SIDEBAR_COLUMN_MAX_PX,
  SIDEBAR_COLUMN_MIN_PX,
} from './sidebarPanes';
import { MobileBottomNav, MOBILE_TAB_BAR_HEIGHT, TABLET_TAB_BAR_CLEARANCE_PX, mobileContentBottomPadding } from './MobileBottomNav';
import { MobileSidebarHost } from './MobileSidebarHost';
import { PageHeaderNavigationProvider } from './PageHeaderNavigationContext';
import {
  MobileShellBottomPaddingProvider,
  MobileShellPaddedProvider,
} from './MobileShellPaddingContext';
import { APP_FRAME_MAX_W, APP_FRAME_PX, MOBILE_SHELL_TOP_PADDING } from './layoutConstants';
import { getRouteChrome, shouldLockMobileViewportScale } from './routeChrome';
import { HeaderSearchSlotProvider } from './HeaderSearchSlot';
import { SidebarResetSlotProvider } from './SidebarResetSlot';
import { getScanModalBackground } from '../../scan/scanModalNavigation';

const SIDEBAR_WIDTH_STORAGE_KEY = 'riftbound_sidebar_width_v2';

/** Max height of the sticky desktop sidebar scroll column below the header (matches ResizeHandle). */
const SIDEBAR_VIEWPORT_BAND = 'calc(100vh - 88px)';

/**
 * Legal footer lives in the filter column on proxy, cards, and collection.
 * Other sidebar routes (match history) keep it under the main column.
 */
function sidebarColumnHasLegalFooter(pathname: string): boolean {
  return (
    pathname === '/card-print' ||
    pathname === '/search' ||
    pathname === '/cards' ||
    pathname.startsWith('/cards/') ||
    pathname === '/collection' ||
    Boolean(matchPath('/collection/sets/:setSlug', pathname))
  );
}

interface Props {
  sidebar?: ReactNode;
  children: ReactNode;
}

function ResizableSidebarBody({ sidebar, children }: Props) {
  const location = useLocation();
  const shell = useSidebarShell();
  const collapsed = Boolean(shell?.enabled && shell.collapsed);
  const rowRef = useRef<HTMLDivElement>(null);
  const sidebarColumnRef = useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = useState(0);
  const [filterCap, setFilterCap] = useState<number | undefined>(undefined);
  const {
    width: sidebarColumnWidth,
    preferredWidth,
    resizing,
    startResize,
  } = useResizableColumn({
    defaultWidth: defaultFilterColumnWidth(),
    minWidth: SIDEBAR_COLUMN_MIN_PX,
    maxWidth: SIDEBAR_COLUMN_MAX_PX,
    availableWidth: filterCap,
    storageKey: SIDEBAR_WIDTH_STORAGE_KEY,
    containerRef: rowRef,
    columnRef: sidebarColumnRef,
  });

  const fit = fitPanes({
    rowWidth,
    filtersOpen: !collapsed,
    filterPreferred: preferredWidth,
    trayRequested: Boolean(shell?.trayRequest?.requested),
    trayPreferred: shell?.trayRequest?.preferredWidth ?? PROXY_TRAY_DEFAULT_PX,
  });

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    const publish = () => {
      const next = Math.round(el.getBoundingClientRect().width);
      setRowWidth((prev) => (prev === next ? prev : next));
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const next = !collapsed && rowWidth > 0 ? fit.filterCap : undefined;
    setFilterCap((prev) => (prev === next ? prev : next));
  }, [collapsed, fit.filterCap, rowWidth]);

  useLayoutEffect(() => {
    const el = sidebarColumnRef.current;
    if (!el || resizing) return;
    el.style.width = collapsed ? '0px' : `${sidebarColumnWidth}px`;
  }, [collapsed, resizing, sidebarColumnWidth]);

  const shownWidth = collapsed ? 0 : sidebarColumnWidth;
  const chrome = {
    sidebarColumnWidth: shownWidth,
    resizeHandleWidth: collapsed ? 0 : SIDEBAR_RESIZE_HANDLE_PX,
    contentGapWidth: collapsed ? 0 : SIDEBAR_CONTENT_GAP_PX,
    rowWidth,
    collapsed,
    trayShown: fit.trayShown,
    trayWidth: fit.trayWidth,
    trayCap: fit.trayCap,
  };

  const shellPathname = getScanModalBackground(location)?.pathname ?? location.pathname;
  /** Proxy, cards, and collection: legal footer scrolls in the filter column. */
  const footerInSidebarColumn = sidebarColumnHasLegalFooter(shellPathname);
  const [resetSlotEl, setResetSlotEl] = useState<HTMLDivElement | null>(null);
  const resetSlot = useMemo(
    () => ({ enabled: footerInSidebarColumn, element: resetSlotEl }),
    [footerInSidebarColumn, resetSlotEl],
  );

  /** Card proxy: main pane needs flex-fill so tray/grid panes get a bounded height (shell uses overflow hidden). */
  const mainChildFlexFill =
    location.pathname === '/card-print'
      ? {
          '& > *': {
            flex: '1 1 0',
            minHeight: 0,
            minWidth: 0,
          },
        }
      : undefined;

  const SidebarColumn = footerInSidebarColumn ? Box : OverlayScroll;

  return (
    <SidebarLayoutProvider value={chrome}>
      <Flex ref={rowRef} gap={0} align="stretch" flex={1} minH={0}>
        <SidebarColumn
          ref={sidebarColumnRef}
          id="catalog-filter-column"
          flexShrink={0}
          minW={0}
          w={collapsed ? '0px' : `${sidebarColumnWidth}px`}
          display={{ base: 'none', md: footerInSidebarColumn ? 'flex' : 'block' }}
          flexDirection={footerInSidebarColumn ? 'column' : undefined}
          position="sticky"
          alignSelf="flex-start"
          top="72px"
          h={footerInSidebarColumn ? SIDEBAR_VIEWPORT_BAND : undefined}
          maxH={SIDEBAR_VIEWPORT_BAND}
          overflowY={footerInSidebarColumn ? 'hidden' : 'auto'}
          overflowX="hidden"
          aria-hidden={collapsed || undefined}
          inert={collapsed || undefined}
          pointerEvents={collapsed ? 'none' : undefined}
          css={{
            transition: resizing ? 'none' : 'width 200ms ease',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none !important',
            },
          }}
        >
          {footerInSidebarColumn ? (
            <SidebarResetSlotProvider value={resetSlot}>
              <OverlayScroll flex="1" minH={0}>
                {sidebar}
                <Box borderTopWidth="hairline" borderColor="border" mt={4} pt={3} pb={1}>
                  <AppFooter variant="sidebar" />
                </Box>
              </OverlayScroll>
              <Box
                ref={setResetSlotEl}
                position="absolute"
                bottom={3}
                left={4}
                right={4}
                zIndex={2}
              />
            </SidebarResetSlotProvider>
          ) : (
            sidebar
          )}
        </SidebarColumn>
        {!collapsed && (
          <ResizeHandle onResizeStart={startResize} hitWidth={SIDEBAR_RESIZE_HANDLE_PX} />
        )}
        <Box
          flex="1"
          minW={0}
          minH={0}
          display="flex"
          flexDirection="column"
          pl={collapsed ? 0 : { base: 0, md: `${SIDEBAR_CONTENT_GAP_PX}px` }}
        >
          <Box
            flex="1"
            minH={0}
            minW={0}
            display="flex"
            flexDirection="column"
            css={mainChildFlexFill}
          >
            {children}
          </Box>
          {(!footerInSidebarColumn || collapsed) && (
            <Box borderTopWidth="hairline" borderColor="border" mt={4} pt={3} pb={1}>
              <AppFooter variant="sidebar" />
            </Box>
          )}
        </Box>
      </Flex>
    </SidebarLayoutProvider>
  );
}

export function AppLayout({ sidebar, children }: Props) {
  const location = useLocation();
  const [headerSearchSlot, setHeaderSearchSlot] = useState<HTMLDivElement | null>(null);
  // Modal scan keeps underlay chrome (tabs/header) mounted beneath the cover.
  const pathname = getScanModalBackground(location)?.pathname ?? location.pathname;
  // ssr:false reads matchMedia on first client paint — avoids mobile shell →
  // desktop sidebar swap that was driving /search CLS (~0.38 in Lighthouse).
  const isDesktop = useBreakpointValue({ base: false, md: true }, { ssr: false }) ?? false;
  const routeChrome = getRouteChrome(pathname);
  const showMobileTabs = routeChrome.showMobileTabs;
  const useFocusedMobileShell = !isDesktop && routeChrome.hideMobileHeader;
  /** Deck editor + profile/team heroes: drop shell px so banners can go edge-to-edge. */
  const flushMobileShellPadding = !isDesktop && routeChrome.flushMobileHorizontalPadding;
  const deckGalleryFabMobile =
    !isDesktop && (pathname === '/decks' || pathname === '/decks/my-decks');
  const matchHistoryFabMobile = !isDesktop && pathname === '/matches';
  const filtersFab = Boolean(sidebar && !isDesktop) || deckGalleryFabMobile || matchHistoryFabMobile;
  const pbMobile = mobileContentBottomPadding({ tabs: showMobileTabs, filtersFab });

  /** Desktop card proxy fills the viewport band under the header (flex); skip shell padding that stacked with a fixed dvh height. */
  const printDesktopDense = pathname === '/card-print' && isDesktop;

  const footerTabLift = showMobileTabs
    ? {
        base: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))` as const,
        md: `calc(${TABLET_TAB_BAR_CLEARANCE_PX}px + env(safe-area-inset-bottom, 0px))` as const,
        lg: 0,
      }
    : { base: 0 as const, md: 0 };

  // The local match counter fills the viewport. Lock document scroll from the
  // shell, not from PlayPage: that page stays mounted in the hidden play tab
  // and in the route-exit tree, and a lock tied to its lifetime sticks after
  // opening Match History or Play.
  const lockMatchDocumentScroll = pathname === '/play/match';
  useEffect(() => {
    if (!lockMatchDocumentScroll) return undefined;

    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyOverscroll = document.body.style.overscrollBehavior;
    const htmlOverscroll = document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overscrollBehavior = bodyOverscroll;
      document.documentElement.style.overscrollBehavior = htmlOverscroll;
    };
  }, [lockMatchDocumentScroll]);

  // Follow the visible URL, not the scan-modal underlay: the camera owns pinch
  // while /collection/scan is open. Desktop and other routes no-op inside the hook.
  useLockMobileViewportScale(shouldLockMobileViewportScale(location.pathname));

  /** Focused routes keep footer/chrome inside their own panels instead of the full-width strip. */
  const hideFullWidthFooter = routeChrome.hideFullWidthFooter;
  const hideFullWidthFooterOnMobile = routeChrome.hideFullWidthFooterOnMobile;

  return (
    <PageHeaderNavigationProvider>
      <HeaderSearchSlotProvider slot={headerSearchSlot} setSlot={setHeaderSearchSlot}>
      <MobileShellPaddedProvider value={!flushMobileShellPadding}>
      <MobileShellBottomPaddingProvider value={pbMobile}>
      <SidebarShellProvider enabled={Boolean(sidebar) && isDesktop}>
      <Flex direction="column" minH="100vh" bg="bg">
        {/* Keep AppHeader mounted on focused mobile shells. Unmounting it on
            /play/match and remounting on the recap made useModals throw. */}
        {useFocusedMobileShell ? (
          <Box display="none" aria-hidden="true">
            <AppHeader />
          </Box>
        ) : (
          <AppHeader />
        )}
        <Box
          flex="1"
          display="flex"
          flexDirection="column"
          minH={0}
          minW={0}
          maxW={APP_FRAME_MAX_W}
          w="100%"
          mx="auto"
          px={flushMobileShellPadding ? { base: 0, xl: 6 } : APP_FRAME_PX}
          pt={
            printDesktopDense
              ? 0
              : { base: useFocusedMobileShell ? 0 : MOBILE_SHELL_TOP_PADDING, md: 4 }
          }
          pb={
            printDesktopDense
              ? 0
              : {
                  base: pbMobile,
                  md: showMobileTabs
                    ? `calc(${TABLET_TAB_BAR_CLEARANCE_PX}px + env(safe-area-inset-bottom, 0px))`
                    : 4,
                  lg: 4,
                }
          }
          /** Desktop card proxy: lock shell scroll so only the three panes scroll (no chained page scroll). */
          overflow={routeChrome.lockDesktopShellScroll ? { base: 'visible', md: 'hidden' } : undefined}
        >
          {isDesktop ? (
            sidebar ? (
              <ResizableSidebarBody sidebar={sidebar}>{children}</ResizableSidebarBody>
            ) : (
              <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" w="100%">
                {children}
              </Box>
            )
          ) : (
            /**
             * Mobile always mounts MobileSidebarHost (even with no sidebar) so the
             * route-transition host beneath it survives sidebar ↔ non-sidebar
             * navigation (e.g. /matches → /matches/:id) instead of remounting and
             * dropping the slide animation.
             */
            <MobileSidebarHost sidebar={sidebar} showBottomTabs={showMobileTabs}>
              {children}
            </MobileSidebarHost>
          )}
        </Box>
        {!hideFullWidthFooter &&
          (sidebar ? (
            /**
             * Sidebar layouts already render AppFooter in the desktop column; on mobile
             * browse tabs the legal strip stays off (app shell). Other mobile sidebar
             * routes still get the full-width footer under the page.
             */
            <Box
              display={{
                base: hideFullWidthFooterOnMobile ? 'none' : 'block',
                md: 'none',
              }}
              w="100%"
              pb={footerTabLift}
            >
              <AppFooter variant="full" />
            </Box>
          ) : (
            <Box
              display={
                hideFullWidthFooterOnMobile ? { base: 'none', md: 'block' } : undefined
              }
              pb={footerTabLift}
            >
              <AppFooter variant="full" />
            </Box>
          ))}
        {showMobileTabs ? <MobileBottomNav /> : null}
      </Flex>
      </SidebarShellProvider>
      </MobileShellBottomPaddingProvider>
      </MobileShellPaddedProvider>
      </HeaderSearchSlotProvider>
    </PageHeaderNavigationProvider>
  );
}
